import React, { useEffect, useMemo, useState } from 'react';
import { Syringe, Search, X, Timer, Cable, Play, FlaskConical, Info, ChevronRight, ChevronLeft, HelpCircle } from 'lucide-react';
import ReactDOM from 'react-dom';
import ToolHeader from '../shared/ToolHeader';
import { Section, Card } from '../hospital/shared/primitives';
import { Segmented, SourceRow } from '../hospital/shared/protocolBits';
import {
  ASRA_EDITION, ASRA_SOURCES, ASRA_CREDIT, ASRA_CLASSES, BLOCK_TYPES, DEEP_BLOCKS, SUPERFICIAL_BLOCKS, SUPERFICIAL_REC, searchAsra, asraDrug,
} from '../../data/clinicalTools/asraAnticoag';
import { trackClinicalToolView } from '../../utils/analyticsUtils';

/*
 * Neuraxial timing for anticoagulated patients. Pick a class, then a drug (the first is
 * open by default so the page is never empty), or search across all of them. One card:
 * hold, catheter, next dose, labs, notes, plus the guideline grade. Pattern follows the
 * THI Case Setup screen. Adapted from Dr Tony Kim's lookup; values from the ASRA fifth
 * edition (see data file).
 */

const ACCENT = '#6d28d9';

const TONE = {
  safe: 'text-emerald-700 dark:text-emerald-300',
  warn: 'text-amber-700 dark:text-amber-300',
  danger: 'text-red-700 dark:text-red-300',
};

const ROWS = [
  { key: 'hold', label: 'Hold before block', icon: Timer },
  { key: 'catheter', label: 'Catheter', icon: Cable },
  { key: 'restart', label: 'Next dose', icon: Play },
  { key: 'labs', label: 'Labs', icon: FlaskConical },
  { key: 'notes', label: 'Notes', icon: Info },
];

const GRADE_HELP = 'Guideline grade. I strong, II weak; A, B, C evidence from high to low';

const DrugCard = ({ drug, superficial }) => {
  const [variant, setVariant] = useState(drug.variants?.[0]?.id);
  useEffect(() => { setVariant(drug.variants?.[0]?.id); }, [drug]);
  const own = drug.variants ? drug.byVariant[variant] || drug.byVariant[drug.variants[0].id] : drug.rec;
  const rec = superficial ? SUPERFICIAL_REC : own;
  return (
    <Card>
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[17px] font-semibold text-gray-900 dark:text-white leading-tight">{drug.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{drug.cls}</div>
        </div>
        <span title={GRADE_HELP} className="flex-shrink-0 px-2 py-0.5 rounded-md text-[11px] font-semibold tracking-wide bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
          {rec.grade}
        </span>
      </div>
      {drug.variants && !superficial && (
        <div className="px-4 py-3">
          <Segmented items={drug.variants} value={variant} onChange={setVariant} small />
        </div>
      )}
      {ROWS.map(({ key, label, icon: Icon }) => {
        const field = rec[key];
        if (!field) return null;
        return (
          <div key={key} className="px-4 py-3 flex gap-3">
            <Icon size={18} className="flex-shrink-0 mt-0.5 text-gray-400 dark:text-gray-500" />
            <div className="min-w-0">
              <div className="eyebrow">{label}</div>
              <div className={`text-[15px] leading-snug mt-0.5 ${TONE[field.tone] || 'text-gray-900 dark:text-white'} ${field.tone ? 'font-medium' : ''}`}>{field.text}</div>
            </div>
          </div>
        );
      })}
      {drug.confirm && !superficial && (
        <div className="px-4 py-2.5 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20">
          Carried from the fourth edition. Confirm against the current guideline.
        </div>
      )}
    </Card>
  );
};


/** Deep versus superficial block lists, as a sheet */
const BlockTypesSheet = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const List = ({ title, note, items }) => (
    <div className="px-5 py-4">
      <div className="eyebrow">{title}</div>
      {note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{note}</div>}
      <ul className="mt-2 space-y-1.5">
        {items.map((b) => <li key={b} className="text-[15px] text-gray-900 dark:text-white leading-snug">{b}</li>)}
      </ul>
    </div>
  );
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn" onClick={onClose}>
      <div role="dialog" aria-label="Deep and superficial blocks" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-4 pb-1">
          <h2 className="font-display text-lg font-semibold text-gray-900 dark:text-white">Deep or superficial?</h2>
          <button onClick={onClose} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} /></button>
        </div>
        <p className="px-5 pb-1 text-sm text-gray-600 dark:text-gray-300 leading-snug">Deep plexus and deep peripheral blocks follow the neuraxial intervals. Superficial, compressible sites have no mandatory interval.</p>
        <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
          <List title="Deep, follow the neuraxial rules" note="Common practice, not a list from the guideline" items={DEEP_BLOCKS} />
          <List title="Usually superficial" items={SUPERFICIAL_BLOCKS} />
        </div>
      </div>
    </div>,
    document.body
  );
};

/** Two-row class switcher, the Case Setup pattern */
const ClassTabs = ({ value, onChange }) => {
  const rows = [ASRA_CLASSES.slice(0, 3), ASRA_CLASSES.slice(3)];
  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 pt-3 pb-3">
        <div className="eyebrow mb-1.5 px-1">Browse by drug class</div>
        <div className="bg-gray-100 dark:bg-gray-900 rounded-xl p-1 space-y-1" role="tablist">
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-3 gap-1">
              {row.map((c) => {
                const active = value === c.id;
                return (
                  <button
                    key={c.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => { onChange(c.id); }}
                    className={`px-2 py-2 rounded-lg text-sm font-semibold transition-all truncate ${
                      active ? 'bg-white dark:bg-gray-700 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                    style={active ? { color: ACCENT } : undefined}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ASRAAnticoagulation = ({ onBack }) => {
  const [classId, setClassId] = useState(ASRA_CLASSES[0].id);
  const [drugId, setDrugId] = useState(null); // null = show the class list
  const [blocksOpen, setBlocksOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [blockType, setBlockType] = useState('neuraxial');
  const superficial = blockType === 'superficial';

  useEffect(() => { trackClinicalToolView('ASRA Anticoagulation'); }, []);

  const searching = query.trim().length > 0;
  const matches = useMemo(() => (searching ? searchAsra(query) : []), [query, searching]);
  const cls = ASRA_CLASSES.find((c) => c.id === classId);
  const drug = drugId ? asraDrug(drugId) : null;

  const pickClass = (id) => {
    const c = ASRA_CLASSES.find((x) => x.id === id);
    setClassId(id);
    setDrugId(c.drugs.length === 1 ? c.drugs[0] : null);
    setQuery('');
  };
  const pickDrug = (id) => { setDrugId(id); };
  const pickMatch = (d) => {
    const owner = ASRA_CLASSES.find((c) => c.drugs.includes(d.id));
    if (owner) setClassId(owner.id);
    setDrugId(d.id);
    setQuery('');
  };

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ToolHeader title="Anticoagulation & Neuraxial" subtitle={ASRA_EDITION} icon={Syringe} accent={ACCENT} backLabel="Clinical Tools" onBack={onBack} />
      <div className="bg-white dark:bg-gray-800 px-4 pb-1">
        <div className="flex items-baseline justify-between mb-1.5 px-1">
          <div className="eyebrow">Block</div>
          <button onClick={() => { setBlocksOpen(true); }} className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: ACCENT }}>
            <HelpCircle size={13} /> Which blocks are deep?
          </button>
        </div>
        <Segmented items={BLOCK_TYPES} value={blockType} onChange={setBlockType} />
        <div className="eyebrow mt-4 mb-1.5 px-1">Search</div>
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search any drug (generic or brand name)"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="w-full pl-11 pr-10 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-900 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="Clear" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400">
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      {!searching && <ClassTabs value={classId} onChange={pickClass} />}
      {blocksOpen && <BlockTypesSheet onClose={() => setBlocksOpen(false)} />}

      <div className="p-6 space-y-5">

        {searching ? (
          <Card>
            {matches.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No match. Try the generic name, the brand name, or a class such as DOAC.</p>
            ) : matches.map((d) => (
              <button key={d.id} onClick={() => pickMatch(d)} className="w-full px-4 py-3 text-left">
                <div className="text-[15px] font-medium text-gray-900 dark:text-white">{d.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{d.cls}</div>
              </button>
            ))}
          </Card>
        ) : (
          <>
            {cls.drugs.length > 1 && !drugId && (
              <Card>
                {cls.drugs.map((id) => {
                  const d = asraDrug(id);
                  return (
                    <button key={id} onClick={() => pickDrug(id)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
                      <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-medium text-gray-900 dark:text-white">{d.name}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.cls}</span>
                      </span>
                      <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    </button>
                  );
                })}
              </Card>
            )}

            {drug && cls.drugs.length > 1 && (
              <button onClick={() => { setDrugId(null); }} className="flex items-center gap-1 text-sm font-medium" style={{ color: ACCENT }}>
                <ChevronLeft size={16} /> All {cls.label.toLowerCase()}
              </button>
            )}

            {drug && <DrugCard drug={drug} superficial={superficial} />}
          </>
        )}

        <Section title="Source">
          <div className="space-y-3">
            {ASRA_SOURCES.map((src) => <SourceRow key={src.href} href={src.href} title={src.title} detail={src.detail} accent={ACCENT} />)}
          </div>
        </Section>
        <p className="px-1 text-xs text-gray-500 dark:text-gray-400">{ASRA_CREDIT}</p>
      </div>
      <div className="h-8" />
    </div>
  );
};

export default ASRAAnticoagulation;
